import React, { useMemo, useState } from 'react';
import { AlertCircle, GitBranch, X } from 'lucide-react';
import { PrefixRestructureModalProps } from './types';
import { createPrefixRestructurePlan, parsePrefixLengthsInput } from './prefixRestructure';

const PrefixRestructureModal: React.FC<PrefixRestructureModalProps> = ({
  open,
  parentNode,
  candidates,
  onClose,
  onConfirm
}) => {
  const [lengthInput, setLengthInput] = useState('');

  React.useEffect(() => {
    if (open) {
      setLengthInput('');
    }
  }, [open]);

  const parsed = useMemo(() => parsePrefixLengthsInput(lengthInput), [lengthInput]);
  const plan = useMemo(() => {
    if (parsed.error || parsed.lengths.length === 0) {
      return null;
    }
    return createPrefixRestructurePlan(candidates, parsed.lengths, parentNode ? [parentNode] : []);
  }, [candidates, parentNode, parsed.error, parsed.lengths]);

  if (!open) return null;

  const previewMatches = plan?.matches.slice(0, 20) || [];
  const previewSkipped = plan?.skipped.slice(0, 5) || [];
  const canConfirm = !parsed.error && parsed.lengths.length > 0 && !!plan && plan.matches.length > 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-in fade-in zoom-in duration-200 border border-slate-200 flex flex-col max-h-[82vh]">
        <div className="p-6 pb-4 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <GitBranch className="text-teal-500" />
              Restructure by Prefix
            </h3>
            <button onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
              <X size={20} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Existing children of{' '}
            <span className="font-semibold text-slate-700">{parentNode?.data.name || 'the selected group'}</span>
            {' '}will be rearranged into prefix paths.
          </p>

          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
            Chunk lengths
          </label>
          <input
            autoFocus
            className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-all font-mono ${
              parsed.error
                ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                : 'border-slate-300 focus:ring-teal-500/20 focus:border-teal-500'
            }`}
            placeholder="2, 3 or 5*"
            value={lengthInput}
            onChange={(e) => setLengthInput(e.target.value)}
          />
          {parsed.error && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle size={13} />
              <span>{parsed.error}</span>
            </div>
          )}
          {!parsed.error && parsed.lengths.length > 0 && plan && (
            <div className="mt-2 text-xs text-slate-500">
              <span className="font-bold text-slate-700">{plan.matches.length}</span> group{plan.matches.length === 1 ? '' : 's'} arranged,
              {' '}
              <span className="font-bold text-slate-700">{plan.generatedPathCount}</span> prefix node{plan.generatedPathCount === 1 ? '' : 's'} created
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 min-h-[120px]">
          {!lengthInput.trim() && (
            <div className="text-center text-sm text-slate-400 py-8">Enter lengths to preview the new prefix paths.</div>
          )}

          {plan && plan.matches.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-8">No eligible groups can be split with these lengths.</div>
          )}

          {previewMatches.length > 0 && (
            <ul className="space-y-1 pb-3">
              {previewMatches.map(match => (
                <li key={match.node.id} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="text-xs text-slate-400 truncate">{match.node.data.name || '(unnamed)'}</div>
                  <div className="text-sm font-medium text-slate-700 truncate">
                    {[...match.generatedPath, match.node.data.name || '(unnamed)'].join(' / ')}
                  </div>
                </li>
              ))}
              {plan && plan.matches.length > previewMatches.length && (
                <li className="text-xs text-slate-400 px-3 py-2">
                  +{plan.matches.length - previewMatches.length} more
                </li>
              )}
            </ul>
          )}

          {previewSkipped.length > 0 && (
            <div className="border-t border-slate-100 pt-3 pb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Skipped</div>
              <ul className="space-y-1">
                {previewSkipped.map(({ node, reason }) => (
                  <li key={node.id} className="text-xs text-slate-500 px-3 py-1.5 rounded bg-slate-50 truncate">
                    <span className="font-medium text-slate-600">{node.data.name || '(unnamed)'}</span>
                    {' - '}
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="p-6 pt-4 flex gap-3 shrink-0 border-t border-slate-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(parsed.lengths)}
            disabled={!canConfirm}
            className={`flex-1 py-2.5 px-4 bg-teal-600 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-teal-600/20 ${
              canConfirm ? 'hover:bg-teal-700 active:scale-95' : 'opacity-50 cursor-not-allowed'
            }`}
          >
            Restructure {plan && plan.matches.length > 0 ? `(${plan.matches.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrefixRestructureModal;
